import React, { useState, useEffect } from "react";
import { doc, setDoc } from "firebase/firestore";
import { signInWithPhoneNumber, signOut, linkWithPhoneNumber, PhoneAuthProvider } from "firebase/auth";
import { httpsCallable } from "firebase/functions";
import { auth, db, setupRecaptcha, resetRecaptcha, clearRecaptchaVerifier, functions, recaptchaConfigReady } from "./firebase";

function ContactCompletion({ onCompletion }) {
  const [area, setArea] = useState("");
  const [contact, setContact] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  
  // SMS Verification states
  const [showSmsVerification, setShowSmsVerification] = useState(false);
  const [verificationCode, setVerificationCode] = useState("");
  const [confirmationResult, setConfirmationResult] = useState(null);
  const [smsError, setSmsError] = useState("");
  const [smsLoading, setSmsLoading] = useState(false);
  const [recaptchaVerifier, setRecaptchaVerifier] = useState(null);

  // Setup reCAPTCHA on component mount
  useEffect(() => {
    let cancelled = false;
    let verifierRef = null;

    (async () => {
      try {
        const verifier = await setupRecaptcha('recaptcha-container');
        if (cancelled) {
          clearRecaptchaVerifier(verifier);
          return;
        }
        verifierRef = verifier;
        setRecaptchaVerifier(verifier);
      } catch (error) {
        console.error('Error setting up reCAPTCHA:', error);
      }
    })();

    return () => {
      cancelled = true;
      clearRecaptchaVerifier(verifierRef);
    };
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    try {
      // Validate fields
      if (!area || !contact) {
        setError("Please fill in all fields.");
        setLoading(false);
        return;
      }

      // Validate contact number (must be exactly 7 digits)
      if (!/^\d{7}$/.test(contact)) {
        setError("Contact number must be exactly 7 digits.");
        setLoading(false);
        return;
      }

      // Initiate SMS verification
      setLoading(false);
      await sendVerificationCode();
    } catch (err) {
      setLoading(false);
      setError(err.message.replace('Firebase: ', ''));
    }
  };

  const sendVerificationCode = async () => {
    setSmsError("");
    setSmsLoading(true);
    
    try {
      const fullPhoneNumber = `+1868${contact}`;
      
      // First, check if phone number is already registered
      const checkPhoneNumber = httpsCallable(functions, 'checkPhoneNumberAvailability');
      const result = await checkPhoneNumber({ phoneNumber: fullPhoneNumber });
      
      if (!result.data.available) {
        setSmsError("This phone number is already registered to another account.");
        setSmsLoading(false);
        return;
      }
      
      const user = auth.currentUser;
      if (!user) {
        setSmsError("User not authenticated.");
        setSmsLoading(false);
        return;
      }
      
      await recaptchaConfigReady;

      let verifier;
      try {
        verifier = await resetRecaptcha(recaptchaVerifier, 'recaptcha-container');
        setRecaptchaVerifier(verifier);
      } catch (setupErr) {
        console.error('Error preparing reCAPTCHA:', setupErr);
        setSmsError('Security check failed to load. Refresh the page and try again.');
        setSmsLoading(false);
        return;
      }

      const confirmation = await linkWithPhoneNumber(user, fullPhoneNumber, verifier);
      setConfirmationResult(confirmation);
      
      setShowSmsVerification(true);
      setSmsLoading(false);
    } catch (err) {
      console.error("SMS Error:", err);
      setSmsLoading(false);
      
      if (err.code === 'auth/invalid-phone-number') {
        setSmsError("Invalid phone number format.");
      } else if (err.code === 'auth/too-many-requests') {
        setSmsError("Too many attempts. Please try again later.");
      } else if (err.code === 'auth/provider-already-linked') {
        setSmsError("This phone number is already linked to this account.");
      } else if (err.code === 'auth/internal-error') {
        setSmsError(
          "Phone verification failed (Firebase/reCAPTCHA temporary error). Refresh and try once more."
        );
      } else {
        setSmsError(err.message.replace('Firebase: ', ''));
      }
      
      try {
        const fresh = await resetRecaptcha(recaptchaVerifier, 'recaptcha-container');
        setRecaptchaVerifier(fresh);
      } catch (error) {
        console.warn('Error resetting reCAPTCHA after SMS failure:', error);
        setRecaptchaVerifier(null);
      }
    }
  };

  const verifyCode = async () => {
    setSmsError("");
    setSmsLoading(true);
    
    try {
      if (!confirmationResult) {
        setSmsError("No verification in progress. Please try again.");
        setSmsLoading(false);
        return;
      }
      
      if (!verificationCode || verificationCode.length !== 6) {
        setSmsError("Please enter a valid 6-digit code.");
        setSmsLoading(false);
        return;
      }
      
      // Verify the SMS code and link to existing Google account
      const user = auth.currentUser;
      if (!user) {
        setSmsError("User not authenticated.");
        setSmsLoading(false);
        return;
      }

      // Link phone number to existing Google account
      const credential = PhoneAuthProvider.credential(confirmationResult.verificationId, verificationCode);
      await linkWithPhoneNumber(user, credential);
      
      // Phone number verified and linked, now update the user profile
      const userDocRef = doc(db, "users", user.uid);
      const fullContactNumber = `+1868${contact}`;
      
      await setDoc(userDocRef, {
        email: user.email,
        name: user.displayName || "",
        area: area,
        contact: fullContactNumber,
        profileCompleted: true,
        phoneVerified: true,
        phoneVerifiedAt: new Date().toISOString()
      }, { merge: true });
      
      setSmsLoading(false);
      onCompletion();
    } catch (err) {
      console.error("Verification Error:", err);
      setSmsLoading(false);
      
      if (err.code === 'auth/invalid-verification-code') {
        setSmsError("Invalid verification code. Please try again.");
      } else if (err.code === 'auth/code-expired') {
        setSmsError("Verification code expired. Please request a new code.");
      } else {
        setSmsError(err.message.replace('Firebase: ', ''));
      }
    }
  };

  const resendVerificationCode = async () => {
    setVerificationCode("");
    setSmsError("");
    setShowSmsVerification(false);
    setConfirmationResult(null);
    await sendVerificationCode();
  };

  const cancelSmsVerification = async () => {
    setShowSmsVerification(false);
    setVerificationCode("");
    setConfirmationResult(null);
    setSmsError("");
    
    // Reset reCAPTCHA
    try {
      const fresh = await resetRecaptcha(recaptchaVerifier, 'recaptcha-container');
      setRecaptchaVerifier(fresh);
    } catch (error) {
      console.warn('Error resetting reCAPTCHA:', error);
      setRecaptchaVerifier(null);
    }
  };

  const handleCancelRegistration = async () => {
    try {
      // Sign out the user
      await signOut(auth);
      // Navigate to homepage using window.location
      window.location.href = '/';
    } catch (error) {
      console.error('Error canceling registration:', error);
      setError('Failed to cancel registration. Please try again.');
    }
  };

  // SMS Verification Modal
  if (showSmsVerification) {
    return (
      <div style={{ minHeight: '100vh', background: '#f9fafb', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ background: '#fff', borderRadius: '12px', boxShadow: '0 4px 16px rgba(0,0,0,0.07)', padding: '2.5rem 2rem', minWidth: 320, maxWidth: 400 }}>
          <h2 style={{ color: '#f97316', fontWeight: 'bold', textAlign: 'center', marginBottom: '1rem' }}>
            Verify Phone Number
          </h2>
          <p style={{ textAlign: 'center', color: '#6b7280', marginBottom: '1.5rem', fontSize: '0.9rem' }}>
            We've sent a 6-digit verification code to<br />
            <strong>+1868{contact}</strong>
          </p>
          
          <input
            type="text"
            placeholder="Enter 6-digit code"
            value={verificationCode}
            onChange={e => setVerificationCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
            style={{ 
              width: '100%', 
              padding: '0.75rem', 
              marginBottom: '1rem', 
              borderRadius: 6, 
              border: '1px solid #e5e7eb', 
              boxSizing: 'border-box',
              fontSize: '1.2rem',
              textAlign: 'center',
              letterSpacing: '0.5rem'
            }}
            autoFocus
            maxLength={6}
          />
          
          {smsError && <div style={{ color: 'red', marginBottom: '1rem', textAlign: 'center', fontSize: '0.9rem' }}>{smsError}</div>}
          
          <button 
            onClick={verifyCode}
            disabled={smsLoading || verificationCode.length !== 6}
            style={{ 
              width: '100%', 
              background: (smsLoading || verificationCode.length !== 6) ? '#9ca3af' : '#f97316', 
              color: '#fff', 
              border: 'none', 
              borderRadius: 6, 
              padding: '0.75rem', 
              fontWeight: 'bold', 
              fontSize: '1rem', 
              cursor: (smsLoading || verificationCode.length !== 6) ? 'not-allowed' : 'pointer', 
              marginBottom: '0.75rem' 
            }}
          >
            {smsLoading ? 'Verifying...' : 'Verify Code'}
          </button>
          
          <button 
            onClick={resendVerificationCode}
            disabled={smsLoading}
            style={{ 
              width: '100%', 
              background: '#fff', 
              color: '#f97316', 
              border: '1px solid #f97316', 
              borderRadius: 6, 
              padding: '0.75rem', 
              fontWeight: 'bold', 
              fontSize: '1rem', 
              cursor: smsLoading ? 'not-allowed' : 'pointer', 
              marginBottom: '0.75rem' 
            }}
          >
            Resend Code
          </button>
          
          <button 
            onClick={cancelSmsVerification}
            disabled={smsLoading}
            style={{ 
              width: '100%', 
              background: 'transparent', 
              color: '#6b7280', 
              border: 'none', 
              borderRadius: 6, 
              padding: '0.5rem', 
              fontSize: '0.9rem', 
              cursor: smsLoading ? 'not-allowed' : 'pointer',
              textDecoration: 'underline'
            }}
          >
            Cancel
          </button>
          
          {/* reCAPTCHA container */}
          <div id="recaptcha-container"></div>
        </div>
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', background: '#f9fafb', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ background: '#fff', borderRadius: '12px', boxShadow: '0 4px 16px rgba(0,0,0,0.07)', padding: '2.5rem 2rem', minWidth: 400 }}>
        <h2 style={{ color: '#f97316', fontWeight: 'bold', textAlign: 'center', marginBottom: '1rem' }}>
          Complete Your Profile
        </h2>
        <p style={{ color: '#6b7280', textAlign: 'center', marginBottom: '2rem', fontSize: '0.95rem' }}>
          Please provide your area and contact number. You'll need to verify your phone number via SMS.
        </p>
        
        <form onSubmit={handleSubmit}>
          <input
            type="text"
            placeholder="Area/Region"
            value={area}
            onChange={e => setArea(e.target.value)}
            style={{ width: '100%', padding: '0.75rem', marginBottom: '1rem', borderRadius: 6, border: '1px solid #e5e7eb', boxSizing: 'border-box' }}
            autoComplete="address-level2"
          />
          
          <div style={{ marginBottom: '0.5rem' }}>
            <div style={{ fontSize: '0.875rem', color: '#6b7280', fontStyle: 'italic' }}>
              Will be used for verification so must be active
            </div>
          </div>
          
          <input
            type="text"
            placeholder="Seven digits - 7654321"
            value={contact}
            onChange={e => setContact(e.target.value.replace(/\D/g, '').slice(0, 7))}
            style={{ width: '100%', padding: '0.75rem', marginBottom: '1rem', borderRadius: 6, border: '1px solid #e5e7eb', boxSizing: 'border-box' }}
            autoComplete="tel"
          />
          
          {error && <div style={{ color: 'red', marginBottom: '1rem', textAlign: 'center' }}>{error}</div>}
          {smsError && <div style={{ color: 'red', marginBottom: '1rem', textAlign: 'center' }}>{smsError}</div>}
          
          <button 
            type="submit" 
            style={{ width: '100%', background: '#f97316', color: '#fff', border: 'none', borderRadius: 6, padding: '0.75rem', fontWeight: 'bold', fontSize: '1rem', cursor: 'pointer', marginBottom: '1rem' }} 
            disabled={loading || smsLoading}
          >
            {loading || smsLoading ? 'Processing...' : 'Continue to Verification'}
          </button>
        </form>
        
        {/* Cancel Registration Button */}
        <button 
          type="button" 
          onClick={handleCancelRegistration}
          style={{ width: '100%', background: '#fff', color: '#dc2626', border: '2px solid #dc2626', borderRadius: 6, padding: '0.75rem', fontWeight: 'bold', fontSize: '1rem', cursor: 'pointer' }} 
          disabled={loading || smsLoading}
        >
          Cancel
        </button>
        
        {/* reCAPTCHA container - invisible */}
        <div id="recaptcha-container"></div>
      </div>
    </div>
  );
}

export default ContactCompletion;
